import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Instagram, MapPin, Send } from "lucide-react";
import { CONTACT_SUBJECTS, SITE, VISIT_INFO } from "@/lib/data";
import { Reveal, SectionHeading } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", contact: "", subject: CONTACT_SUBJECTS[0], message: "" };

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Pesan terkirim", { description: "Terima kasih. Pengurus DKM akan menghubungi Anda secepatnya." });
      setForm(EMPTY);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error("Pesan belum terkirim", {
        description: typeof detail === "string" ? detail : "Periksa kembali isian Anda—pesan minimal 10 karakter.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="kontak" className="relative bg-sand-50 py-28 lg:py-36" data-testid="contact-section">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Lokasi & Kontak"
              title={
                <>
                  Kami menanti Anda <em>di tepi</em> Selat Bali.
                </>
              }
              description="Untuk jadwal manasik, kunjungan rombongan, konfirmasi donasi, maupun kerja sama—kirimkan pesan Anda. Setiap pesan tersimpan dan dibaca oleh pengurus."
            />
            <div className="mt-12 space-y-8">
              {VISIT_INFO.map((v, i) => (
                <Reveal key={v.title} delay={0.1 + i * 0.08}>
                  <div className="border-t border-navy/10 pt-6">
                    <p className="eyebrow text-teal">{v.title}</p>
                    {v.lines.map((l) => (
                      <p key={l} className="mt-1 text-sm text-navy md:text-base">
                        {l}
                      </p>
                    ))}
                  </div>
                </Reveal>
              ))}
              <Reveal delay={0.35}>
                <div className="flex flex-wrap gap-3">
                  <a href={SITE.instagram} target="_blank" rel="noopener noreferrer" className="btn-ghost !px-5 !py-3" data-testid="instagram-link">
                    <Instagram className="h-4 w-4" /> {SITE.instagramHandle}
                  </a>
                  <a href={SITE.mapsLink} target="_blank" rel="noopener noreferrer" className="btn-ghost !px-5 !py-3" data-testid="maps-link">
                    <MapPin className="h-4 w-4" /> Petunjuk arah
                  </a>
                </div>
              </Reveal>
            </div>
          </div>

          <Reveal delay={0.2} className="lg:col-span-7">
            <form onSubmit={submit} className="rounded-[2.5rem] border border-navy/10 bg-white/80 p-7 backdrop-blur sm:p-10" data-testid="contact-form">
              <div className="grid gap-5 sm:grid-cols-2">
                <label className="block">
                  <span className="eyebrow text-teal">Nama lengkap</span>
                  <input required minLength={2} value={form.name} onChange={update("name")} placeholder="Nama Anda" className="field mt-2" data-testid="contact-name-input" />
                </label>
                <label className="block">
                  <span className="eyebrow text-teal">Email / WhatsApp</span>
                  <input required minLength={5} value={form.contact} onChange={update("contact")} placeholder="email@contoh.com atau 08xx" className="field mt-2" data-testid="contact-contact-input" />
                </label>
              </div>
              <label className="mt-5 block">
                <span className="eyebrow text-teal">Subjek</span>
                <select value={form.subject} onChange={update("subject")} className="field mt-2 appearance-none" data-testid="contact-subject-select">
                  {CONTACT_SUBJECTS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
              <label className="mt-5 block">
                <span className="eyebrow text-teal">Pesan</span>
                <textarea required minLength={10} rows={6} value={form.message} onChange={update("message")} placeholder="Tuliskan pesan Anda…" className="field mt-2 resize-none" data-testid="contact-message-input" />
              </label>
              <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
                <p className="text-xs text-slate-500">Dengan mengirim pesan, Anda setuju dihubungi oleh pengurus DKM.</p>
                <button type="submit" disabled={sending} className="btn-primary disabled:cursor-wait disabled:opacity-70" data-testid="contact-submit-button">
                  {sending ? "Mengirim…" : "Kirim Pesan"} <Send className="h-4 w-4" />
                </button>
              </div>
            </form>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="mt-20">
          <div className="relative overflow-hidden rounded-[2.5rem] rounded-tl-[8rem] border border-navy/10 bg-sand-100" data-testid="map-frame">
            <iframe
              title="Peta lokasi Masjid Pantai Bali"
              src={SITE.mapsEmbed}
              className="h-[420px] w-full grayscale-[30%] transition-[filter] duration-700 hover:grayscale-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
            <div className="glass pointer-events-none absolute bottom-6 left-6 hidden max-w-xs rounded-3xl p-5 text-navy sm:block">
              <p className="eyebrow text-teal">Masjid Pantai Bali</p>
              <p className="mt-1 text-sm">{SITE.address}</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
